import { useQuery, gql } from "@apollo/client"
import { offsetLimitPagination } from "@apollo/client/utilities"
import { SENSOR_DEVICE_FIELDS } from "./fragments/sensorFieldsFragment"

const INTEGRATION_SENSORS_LIMIT = 60

const createIntegrationSensorsQuery = (name, pathName) => gql`
  query ${name}(
    $integration: ID!
    $filter: IntegrationSensorsFilterInput
    $orderBy: IntegrationSensorsOrderByInput
    $offset: Int
    $limit: Int
  ) {
    integration(id: $integration) {
      sensors {
        ${pathName} {
          list(filter: $filter, orderBy: $orderBy, limit: $limit, offset: $offset) {
            ...SensorDeviceFields
          }
          count(filter: $filter)
        }
      }
    }
  }
  ${SENSOR_DEVICE_FIELDS}
`

const INTEGRATION_SENSORS_LATEST_READINGS_QUERY = createIntegrationSensorsQuery(
  "IntegrationSensorsLatestReadings",
  "latestReadings"
)

const INTEGRATION_SENSORS_IN_ALERT_QUERY = createIntegrationSensorsQuery("IntegrationSensorsInAlert", "inAlert")

/**
 * Computes the cache key for the list query
 * @param {Object} args - The arguments object
 * @returns {string} - The cache key
 */
const computeListCacheKey = (args) => JSON.stringify(args.filter) + JSON.stringify(args.orderBy)

/**
 * Defines the type policies for the integration sensors latest readings & in alert query types
 */
const integrationSensorsTypePolicies = {
  scoped: {
    IntegrationSensorsLatestReadingsQuery: {
      merge(prev, next) {
        return {
          ...(prev || {}),
          ...next
        }
      },
      fields: {
        list: offsetLimitPagination(computeListCacheKey),
        count: {
          keyArgs: ["filter"]
        }
      }
    },
    IntegrationSensorsInAlertQuery: {
      merge(prev, next) {
        return {
          ...(prev || {}),
          ...next
        }
      },
      fields: {
        list: offsetLimitPagination(computeListCacheKey),
        count: {
          keyArgs: ["filter"]
        }
      }
    }
  }
}

const loadMore = (pathName, result) => async () => {
  try {
    await result.fetchMore({
      variables: {
        ...result.variables,
        offset: result.data.integration?.sensors?.[pathName]?.list?.length || 0
      }
    })
  } catch (error) {
    console.log(error)
  }
}

const useQueryIntegrationSensors = (pathName, query, options) => {
  const result = useQuery(query, {
    ...options,
    variables: {
      ...options.variables,
      offset: 0,
      limit: options?.variables?.limit || INTEGRATION_SENSORS_LIMIT
    }
  })

  return {
    ...result,
    loadMore: loadMore(pathName, result)
  }
}

const useQueryIntegrationSensorsLatestReadings = (options) => {
  return useQueryIntegrationSensors("latestReadings", INTEGRATION_SENSORS_LATEST_READINGS_QUERY, options)
}

const useQueryIntegrationSensorsInAlert = (options) => {
  return useQueryIntegrationSensors("inAlert", INTEGRATION_SENSORS_IN_ALERT_QUERY, options)
}

export { useQueryIntegrationSensorsLatestReadings, useQueryIntegrationSensorsInAlert, integrationSensorsTypePolicies }
